import { useMemo } from "react";
import useCustomFilters, {
  FilterOption,
} from "components/custom-filters-list/filters/use-custom-filters.hook";

import SelectClinic from "components/custom-filters-list/filters/select-clinic.component";
import * as React from "react";
import { ReportFilter } from "features/reports/types";
import SelectAppointmentType from "components/custom-filters-list/filters/select-appointment-type.component";
import SelectBooleanValue from "components/custom-filters-list/filters/select-boolean-value.component";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import SelectScribeTemplate from "components/custom-filters-list/filters/select-scribe-template.component";
import type { AmbientScribeFilters } from "services/types/ambient-scribe.type";
import SelectPatient from "components/custom-filters-list/filters/select-patient.component";

export type ScribesCustomCustomFiltersValues = {
  clinic?: ReportFilter<string>;
  staff?: ReportFilter<string>;
  patient?: ReportFilter<string>;
  appointment_type?: ReportFilter<string>;
  ambient_scribe_template?: ReportFilter<string>;
  has_chart_note?: ReportFilter<boolean>;
};

export const SCRIBES_DEFAULT_FILTERS: ScribesCustomCustomFiltersValues = {
  clinic: null,
  staff: null,
  patient: null,
  appointment_type: null,
  ambient_scribe_template: null,
  has_chart_note: null,
};

/**
 * Labels for the chart-note filter. A scribe has no chart note when it was an ad-hoc recording,
 * or when the recording-time link never resolved — so "Not linked" is a real, useful cohort
 * (those recordings never reached the patient's record), not an error state.
 */
const CHART_NOTE_OPTIONS = [
  { label: "Linked", value: true },
  { label: "Not linked", value: false },
];

type UseScribeCustomFilterOptionsProps = {
  updateCustomFilter: (
    filterName: keyof AmbientScribeFilters,
    filterValue:
      | AmbientScribeFilters[keyof AmbientScribeFilters]
      | undefined
      | null
  ) => void;
  selectedCustomFilters: ScribesCustomCustomFiltersValues;
  setSelectedCustomFilters: React.Dispatch<
    React.SetStateAction<ScribesCustomCustomFiltersValues>
  >;
  /**
   * AMBIENT_SCRIBE_LIST_FILTERS. Off => the appointment-type / template / chart-note filters are
   * not offered at all, so the request params are byte-identical to before this change.
   */
  areListFiltersEnabled?: boolean;
};

export default function useScribeCustomFilterOptions({
  updateCustomFilter,
  selectedCustomFilters,
  setSelectedCustomFilters,
  areListFiltersEnabled = false,
}: UseScribeCustomFilterOptionsProps) {
  const filterOptions: FilterOption<
    ScribesCustomCustomFiltersValues,
    AmbientScribeFilters
  >[] = useMemo(
    () => [
      {
        name: "staff",
        label: "Staff",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Staff: <b>{metadata.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectProvider
            name="staff"
            control={control}
            label="By Staff"
            autoFocus
            onOptionSelected={(option) => {
              updateFilter(option?.value ?? null);
              setMetadata(option);
            }}
            openOnFocus
            includeOtherClinics
            ignoreRegionsFilter
            ignoreClinicsFilter
          />
        ),
      },
      {
        name: "patient",
        label: "Patient",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Patient: <b>{metadata?.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectPatient
            name="patient"
            control={control}
            onOptionSelected={(selected) => {
              if (selected) {
                updateFilter(selected.value);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
            showInactive
          />
        ),
      },
      {
        name: "clinic",
        label: "Clinic",
        renderViewMode: (filterKey, metadata) => {
          return (
            <span>
              By Clinic: <b>{metadata?.label}</b>
            </span>
          );
        },
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectClinic
            name="clinic"
            showAllRegions
            control={control}
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");
                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
            filterByRegions={[]}
          />
        ),
      },
      ...(areListFiltersEnabled
        ? ([
            {
              name: "appointment_type",
              label: "Appointment Type",
              renderViewMode: (filterKey, metadata) => (
                <span>
                  By Appointment Type: <b>{metadata?.label}</b>
                </span>
              ),
              editComponent: (
                filterKey,
                setMetadata,
                control,
                updateFilter
              ) => (
                <SelectAppointmentType
                  name="appointment_type"
                  control={control}
                  label="By Appointment Type"
                  // `null` = every type, active or not. A scribe recorded months ago can point at
                  // a type that has since been retired; excluding those would make its own rows
                  // unfilterable. `SelectAppointmentType` marks them "(disabled)" in the list.
                  isActive={null}
                  onOptionSelected={(selected) => {
                    updateFilter(selected?.value ?? null);
                    setMetadata(selected);
                  }}
                />
              ),
            },
            {
              name: "ambient_scribe_template",
              label: "Template",
              renderViewMode: (filterKey, metadata) => (
                <span>
                  By Template: <b>{metadata?.label}</b>
                </span>
              ),
              editComponent: (
                filterKey,
                setMetadata,
                control,
                updateFilter
              ) => (
                <SelectScribeTemplate
                  name="ambient_scribe_template"
                  control={control}
                  label="By Template"
                  onOptionSelected={(selected) => {
                    const option = Array.isArray(selected)
                      ? selected[0]
                      : selected;
                    updateFilter(option?.value ?? null);
                    setMetadata(option ?? null);
                  }}
                />
              ),
            },
            {
              name: "has_chart_note",
              label: "Chart Note",
              renderViewMode: (filterKey, metadata) => (
                <span>
                  Chart Note: <b>{metadata?.label}</b>
                </span>
              ),
              editComponent: (
                filterKey,
                setMetadata,
                control,
                updateFilter
              ) => (
                <SelectBooleanValue
                  name="has_chart_note"
                  control={control}
                  label="Chart Note"
                  options={CHART_NOTE_OPTIONS}
                  onOptionSelected={(selected) => {
                    // `false` is a real selection ("Not linked"), so this cannot collapse to a
                    // truthiness check — only an absent option clears the filter.
                    updateFilter(selected ? selected.value : null);
                    setMetadata(selected);
                  }}
                />
              ),
            },
          ] as FilterOption<
            ScribesCustomCustomFiltersValues,
            AmbientScribeFilters
          >[])
        : []),
    ],
    [areListFiltersEnabled]
  );

  const filters = useCustomFilters<
    ScribesCustomCustomFiltersValues,
    AmbientScribeFilters
  >({
    updateCustomFilter,
    filterOptions,
    selectedCustomFilters,
    setSelectedCustomFilters,
  });

  return filters;
}
